import React from "react";
import { Chip } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";

export type StatusChipType = 'issue' | 'missing' | 'warning' | 'ok';

interface StatusChipProps {
  type: StatusChipType;
  value?: boolean | string | number | null;
  customLabel?: string;
}

// Standalone version for direct use
export const StatusChip: React.FC<StatusChipProps> = ({ 
  type, 
  value, 
  customLabel 
}) => {
  const getChipProps = () => {
    switch (type) {
      case 'missing':
        return {
          label: customLabel || "MISSING",
          color: "error" as const,
          variant: "filled" as const,
          fontWeight: 'bold'
        };
      case 'issue':
        return {
          label: customLabel || "ISSUE",
          color: "error" as const,
          variant: "filled" as const,
          fontWeight: 'bold'
        };
      case 'warning':
        return {
          label: customLabel || "WARNING",
          color: "warning" as const,
          variant: "filled" as const,
          fontWeight: 'bold'
        };
      case 'ok':
        return {
          label: customLabel || "OK",
          color: "success" as const,
          variant: "outlined" as const,
          fontWeight: 'normal'
        };
      default:
        return {
          label: "OK",
          color: "success" as const,
          variant: "outlined" as const,
          fontWeight: 'normal'
        };
    }
  };

  const chipProps = getChipProps();

  return (
    <Chip
      label={chipProps.label}
      size="small"
      color={chipProps.color}
      variant={chipProps.variant}
      sx={{ 
        fontSize: '0.75rem',
        height: '20px',
        '& .MuiChip-label': {
          px: 1,
          fontWeight: chipProps.fontWeight
        }
      }}
    />
  );
};

// DataGrid renderer version for GridRenderCellParams
export const renderStatusChip = (
  type: StatusChipType, 
  customLabel?: string
) => (params: GridRenderCellParams) => {
  return <StatusChip type={type} value={params.value} customLabel={customLabel} />;
};

// Backward compatibility - enhanced version of ComplianceIssueBooleanIcon
export const FlexibleComplianceIcon = (
  issueLabel: string = "ISSUE",
  okLabel: string = "OK",
  missingLabel: string = "MISSING"
) => (params: GridRenderCellParams) => {
  const value = params.value;
  
  // Handle null/undefined as missing
  if (value === null || value === undefined || value === "") {
    return <StatusChip type="missing" customLabel={missingLabel} />;
  }
  
  // Handle boolean values
  if (typeof value === "boolean") {
    return <StatusChip type={value ? "issue" : "ok"} customLabel={value ? issueLabel : okLabel} />;
  }
  
  // Handle numeric values (e.g., count < 1 = missing/issue)
  if (typeof value === "number") {
    if (value === 0) {
      return <StatusChip type="missing" customLabel={missingLabel} />;
    }
    return <StatusChip type="ok" customLabel={okLabel} />;
  }
  
  // Handle string values
  if (typeof value === "string") {
    if (value.toLowerCase() === "missing" || value.trim() === "" || value === "N/A") {
      return <StatusChip type="missing" customLabel={missingLabel} />;
    }
    return <StatusChip type="ok" customLabel={okLabel} />;
  }
  
  return null;
};
